import _ from 'lodash'
import React, { ReactNode } from 'react'
import PropTypes from 'prop-types'
import { Box, Skeleton, Typography } from '@mui/material'

import { FromNow } from 'src/date'
import { rotateIcon } from 'src/designSystem/icons'
import { isSuccess, useFirebase } from 'src/firebase'
import { ConnectionData, OnlineStatus as OnlineStatusData, PrivateUserData } from 'src/legacy/models/firebase'
import { useGetPatientCreatedAtQuery } from 'src/participant/patientQueries.generated'

const PARTICIPANT_IS_ONLINE_TIMEOUT = 60 * 60 * 1000 // if participant's lastActivity is over 60 min ago, timeout

const isActiveConnection = (connection: ConnectionData) => {
  const oldestActivityAllowed = Date.now() - PARTICIPANT_IS_ONLINE_TIMEOUT
  return connection.lastActivity >= oldestActivityAllowed
}

const getLastSeenAt = (onlineStatus: OnlineStatusData | null) => {
  const lastConnection = _.chain(onlineStatus).get('connections').values().maxBy('lastActivity').value()
  if (lastConnection && onlineStatus && lastConnection.lastActivity > onlineStatus.lastSeenAt) {
    return lastConnection.lastActivity
  }
  return _.get(onlineStatus, 'lastSeenAt')
}

type Props = {
  uid: string
}

const OnlineStatus = ({ uid }: Props) => {
  const onlineStatusRequest = useFirebase<OnlineStatusData>(`onlineStatus/${uid}`)
  const privateUserDataRequest = useFirebase<PrivateUserData>(`privateUserData/${uid}`)
  const patientWithCreatedAt = useGetPatientCreatedAtQuery({ variables: { uid } })

  if (!isSuccess(onlineStatusRequest) || !isSuccess(privateUserDataRequest) || patientWithCreatedAt.loading) {
    return <Skeleton />
  }

  const onlineStatus = onlineStatusRequest.data
  const privateUserData = privateUserDataRequest.data

  const activityRow = (label: string, value: ReactNode) => (
    <Box display="flex" alignItems="center" gap={1}>
      <img src={rotateIcon} alt="" width={16} height={16} />
      <Typography noWrap variant="caption">
        {label} <strong>{value}</strong>
      </Typography>
    </Box>
  )

  const isOnline = _.chain(onlineStatus).get('connections').some(isActiveConnection).value()
  if (isOnline) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Box width={16} height={16} display="flex" alignItems="center" justifyContent="center">
          <Box width={8} height={8} borderRadius="50%" bgcolor="primary.main" />
        </Box>
        <Typography noWrap variant="caption" color="primary.main">
          Online
        </Typography>
      </Box>
    )
  }
  const lastSeenAt = getLastSeenAt(onlineStatus)
  if (lastSeenAt) {
    return activityRow('Last seen:', <FromNow value={lastSeenAt} />)
  }
  const lastLoginAt = _.get(privateUserData, 'lastLoginAt')
  if (lastLoginAt) {
    return activityRow('Last seen:', <FromNow value={lastLoginAt} />)
  }
  const createdAt = patientWithCreatedAt.data?.patient.createdAt
  return activityRow('Created at:', <FromNow value={createdAt} />)
}

OnlineStatus.propTypes = {
  uid: PropTypes.string.isRequired,
}

export default OnlineStatus
